/**
 * Incoming-call leader election  (ADDITIVE — INBOUND CALLING, Phase IV)
 *
 * PROBLEM: a user with the CRM open in four tabs has four Twilio Devices
 * registered under the SAME identity. Twilio will offer an incoming call to all
 * of them, four modals ring, and whichever tab the user happens to click wins a
 * race with the other three. Worse, two tabs can both call `accept()`.
 *
 * SOLUTION: exactly one tab holds a lease. Only the leader registers incoming
 * handling and only the leader may show a modal or accept a call.
 *
 * SCOPE: leadership is per (application='crm', workspaceId, userId). Switching
 * workspace or user is a DIFFERENT lease — the old one is released, and a tab
 * that is leader for workspace A is not leader for workspace B.
 *
 * TRANSPORT: BroadcastChannel. Only small JSON coordination messages cross it.
 * A Twilio Call object is NOT serializable and is NEVER sent — the Call stays
 * in the leader tab's memory. Followers learn only that *a* call was
 * detected/accepted/rejected/canceled, never how to touch it.
 *
 * CONFLICT RESOLUTION is deterministic, so two tabs claiming simultaneously
 * always agree on the winner without a negotiation round-trip:
 *   1. older `claimedAt` wins  (the tab that has been trying longest)
 *   2. tie → lexicographically smaller `tabId` wins  (stable, arbitrary, agreed)
 * Both tabs run the same comparison on the same two values, so they cannot
 * disagree. This is what prevents split-brain.
 *
 * LIVENESS: the leader heartbeats every HEARTBEAT_MS. A follower that has not
 * seen a heartbeat for STALE_AFTER_MS assumes the leader is gone (tab closed,
 * crashed, or suspended) and claims. A closing tab also broadcasts an explicit
 * release, so the normal path is instant rather than waiting out the timeout.
 */

export const HEARTBEAT_MS = 2000;
export const STALE_AFTER_MS = 6000; // 3 missed heartbeats

export type LeaderMessage =
  | { type: 'leader_claim'; scope: string; tabId: string; claimedAt: number }
  | { type: 'leader_heartbeat'; scope: string; tabId: string; claimedAt: number }
  | { type: 'leader_release'; scope: string; tabId: string }
  /** A call is ringing in the leader. Followers use this only to stay quiet. */
  | { type: 'incoming_detected'; scope: string; tabId: string; callSid?: string }
  | { type: 'incoming_accepted'; scope: string; tabId: string; callSid?: string }
  | { type: 'incoming_rejected'; scope: string; tabId: string; callSid?: string }
  | { type: 'incoming_canceled'; scope: string; tabId: string; callSid?: string };

export type SuppressionMessage = Extract<
  LeaderMessage,
  { type: 'incoming_detected' | 'incoming_accepted' | 'incoming_rejected' | 'incoming_canceled' }
>;

/** Leadership is scoped — a lease for one workspace says nothing about another. */
export function buildScope(workspaceId: string, userId: string): string {
  return `crm:${workspaceId}:${userId}`;
}

/** Stable per-tab id. Random is fine: it only needs to be unique and comparable. */
export function createTabId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `tab_${rand}`;
}

/**
 * Deterministic winner between two claims. Same inputs → same answer in every
 * tab, which is the entire anti-split-brain guarantee.
 *
 * @returns true when `a` beats `b`.
 */
export function claimWins(
  a: { tabId: string; claimedAt: number },
  b: { tabId: string; claimedAt: number }
): boolean {
  if (a.claimedAt !== b.claimedAt) return a.claimedAt < b.claimedAt;
  return a.tabId < b.tabId;
}

export interface LeaderElectionCallbacks {
  /** Called when this tab gains or loses leadership. */
  onLeadershipChange: (isLeader: boolean) => void;
  /** Called on a suppression message from ANOTHER tab. */
  onRemoteSuppression: (msg: SuppressionMessage) => void;
}

export interface LeaderElection {
  readonly tabId: string;
  readonly scope: string;
  isLeader: () => boolean;
  /** Broadcast a suppression event to the other tabs. Leader-only in practice. */
  broadcast: (msg: Omit<SuppressionMessage, 'scope' | 'tabId'>) => void;
  /** Release leadership + tear down every listener and timer. Idempotent. */
  destroy: () => void;
}

/**
 * Start participating in the election for `scope`.
 *
 * Degrades honestly: in a browser with no BroadcastChannel (or in a test env
 * without one), the tab simply declares itself leader. Single-tab behaviour is
 * then correct, and multi-tab degrades to today's behaviour rather than
 * breaking.
 */
export function startLeaderElection(
  scope: string,
  callbacks: LeaderElectionCallbacks
): LeaderElection {
  const tabId = createTabId();
  const claimedAt = Date.now();

  let leader = false;
  let destroyed = false;
  let lastLeaderSeenAt = 0;
  /** The claim we currently believe is winning, if it isn't ours. */
  let rivalClaim: { tabId: string; claimedAt: number } | null = null;

  let heartbeatTimer: number | null = null;
  let watchdogTimer: number | null = null;

  const setLeader = (next: boolean) => {
    if (leader === next || destroyed) return;
    leader = next;
    callbacks.onLeadershipChange(next);
  };

  const hasBC = typeof BroadcastChannel !== 'undefined';
  const channel: BroadcastChannel | null = hasBC
    ? new BroadcastChannel(`levelup.telephony.${scope}`)
    : null;

  const post = (msg: LeaderMessage) => {
    if (!channel || destroyed) return;
    try {
      channel.postMessage(msg);
    } catch {
      // A closed channel must not take the call down with it.
    }
  };

  const claim = () => {
    if (destroyed) return;
    post({ type: 'leader_claim', scope, tabId, claimedAt });
    // Optimistically lead. If a better claim exists, its owner will answer with
    // its own claim/heartbeat and we will stand down below. The window is one
    // message round-trip, and the deterministic comparison guarantees we both
    // reach the same conclusion.
    setLeader(true);
  };

  const standDownTo = (rival: { tabId: string; claimedAt: number }) => {
    rivalClaim = rival;
    lastLeaderSeenAt = Date.now();
    setLeader(false);
  };

  if (channel) {
    channel.onmessage = (event: MessageEvent<LeaderMessage>) => {
      const msg = event.data;
      if (destroyed || !msg || msg.scope !== scope || msg.tabId === tabId) return;

      switch (msg.type) {
        case 'leader_claim':
        case 'leader_heartbeat': {
          const rival = { tabId: msg.tabId, claimedAt: msg.claimedAt };
          const mine = { tabId, claimedAt };
          if (claimWins(mine, rival)) {
            // We win. Assert it so the rival stands down.
            rivalClaim = null;
            if (!leader) setLeader(true);
            post({ type: 'leader_heartbeat', scope, tabId, claimedAt });
          } else {
            standDownTo(rival);
          }
          break;
        }
        case 'leader_release': {
          // The leader is going away. Only react if it was the tab we deferred
          // to — a release from an unrelated tab means nothing.
          if (rivalClaim && rivalClaim.tabId === msg.tabId) {
            rivalClaim = null;
            claim();
          }
          break;
        }
        case 'incoming_detected':
        case 'incoming_accepted':
        case 'incoming_rejected':
        case 'incoming_canceled':
          callbacks.onRemoteSuppression(msg);
          break;
      }
    };
  }

  // Kick off.
  claim();

  if (channel) {
    heartbeatTimer = window.setInterval(() => {
      if (destroyed) return;
      if (leader) post({ type: 'leader_heartbeat', scope, tabId, claimedAt });
    }, HEARTBEAT_MS);

    watchdogTimer = window.setInterval(() => {
      if (destroyed || leader || !rivalClaim) return;
      // We are a follower. If the leader has gone quiet, take over.
      if (Date.now() - lastLeaderSeenAt > STALE_AFTER_MS) {
        rivalClaim = null;
        claim();
      }
    }, HEARTBEAT_MS);
  }

  const release = () => {
    if (leader) post({ type: 'leader_release', scope, tabId });
  };

  // A closed tab should hand off immediately rather than making the survivors
  // wait out the staleness timeout.
  const onPageHide = () => release();
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
  }

  return {
    tabId,
    scope,
    isLeader: () => leader,
    broadcast: (msg) => {
      post({ ...msg, scope, tabId } as LeaderMessage);
    },
    destroy: () => {
      if (destroyed) return;
      release();
      destroyed = true;
      if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
      if (watchdogTimer !== null) window.clearInterval(watchdogTimer);
      heartbeatTimer = null;
      watchdogTimer = null;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onPageHide);
      }
      if (channel) {
        channel.onmessage = null;
        try {
          channel.close();
        } catch {
          /* ignore */
        }
      }
      leader = false;
    },
  };
}
